import { action, makeObservable, observable } from 'mobx'

import NoAgent from '@/contentComponents/modal/NoAgent'
import { printController } from '@/controller/print.controller'
import { PrintAgent, PrintOption } from '@/dto'
import commonRepo from '@/repository/common.repo'
import { toastError } from '@/utils/toast'

import modalService from '../modal.service'
import { localStorageService } from '../storage.service'

export class CommonPrintService {
  printOption = new PrintOption()
  printAgent?: PrintAgent
  studentIdList: string[] = []

  static MAX_PRINT_QUANTITY = 50

  constructor() {
    makeObservable<CommonPrintService, 'setPrintOption' | 'printErrorNotice'>(this, {
      printOption: observable,
      printAgent: observable,
      studentIdList: observable,
      setPrintOption: action,
      selectPrinter: action,
      getDividedProblemAndAnswerPage: action,
      fetchPrintList: action,
      printErrorNotice: action,
    })
  }

  selectPrinter = (printer: PrintAgent['printerList'][number]) => {
    localStorageService.setItem('SELECTED_PRINTER')(printer)
    this.printAgent?.update({ selectedPrinter: printer })
    this.printOption.update({
      machineId: printer.machineId,
      printer: printer.printerName,
    })
  }

  getDividedProblemAndAnswerPage(pageText: string, devidingPageArr: number[]) {
    let resultPageText = pageText

    for (const devidingPage of devidingPageArr) {
      const resultPageArr: string[] = []
      const pageTextArr = resultPageText.split(',')

      pageTextArr.forEach((page) => {
        const pageRangeArr = page.split('-')

        if (pageRangeArr.length > 1) {
          if (devidingPage === parseInt(pageRangeArr[1])) {
            resultPageArr.push(page)
            return
          }
          if (devidingPage === parseInt(pageRangeArr[0])) {
            resultPageArr.push(pageRangeArr[0])
            resultPageArr.push(
              devidingPage + 1 === parseInt(pageRangeArr[1])
                ? pageRangeArr[1]
                : `${devidingPage + 1}-${pageRangeArr[1]}`,
            )
            return
          }
          if (devidingPage + 1 === parseInt(pageRangeArr[1])) {
            resultPageArr.push(`${pageRangeArr[0]}-${devidingPage}`)
            resultPageArr.push(pageRangeArr[1])
            return
          }
          if (
            devidingPage > parseInt(pageRangeArr[0]) &&
            devidingPage < parseInt(pageRangeArr[1])
          ) {
            resultPageArr.push(`${pageRangeArr[0]}-${devidingPage}`)
            resultPageArr.push(`${devidingPage + 1}-${pageRangeArr[1]}`)
            return
          }
          resultPageArr.push(page)
        } else {
          resultPageArr.push(page)
        }
      })

      resultPageText = resultPageArr.toString()
    }
    return resultPageText
  }

  // 내부적으로 error handing하고 성공/실패 여부를 반환
  async fetchPrintList(): Promise<boolean> {
    const teacherId = commonRepo.user?.teacherId
    if (!teacherId) {
      toastError('선생님 정보를 찾을 수 없습니다.')
      return false
    }

    try {
      const agentData = await printController.fetchPrintAgentList(teacherId)
      const printAgent = new PrintAgent(agentData)

      // 연결된 컴퓨터가 없거나, 연결된 컴퓨터에 프린트가 없으면 에러처리
      if (!agentData.length || !printAgent.printerList.length) {
        toastError('프린터가 연결되지 않았습니다.')
        return false
      }

      this.printAgent = printAgent
      const recentPrintOption = localStorageService.getItem('RECENT_COMMON_PRINT_OPTION')

      // 최근 인쇄한 프린터 정보가 있으면 업데이트
      if (recentPrintOption) {
        this.printOption.update({ ...recentPrintOption })

        // 업데이트시 프린터 리스트에 해당 프린터가 없으면 첫번째로 선택
        const target = printAgent.printerList.find(
          ({ machineId }) => machineId === this.printOption.machineId,
        )
        if (!target) {
          this.printOption.update({
            printer: printAgent.printerList[0].printerName,
          })
        }
      }

      this.selectPrinter(this.printAgent!.selectedPrinter!)
      return true
    } catch (e) {
      this.printErrorNotice(e)
    }
    return false
  }

  protected setPrintOption() {
    const { copyNumber, ...params } = this.printOption.params

    localStorageService.setItem('RECENT_COMMON_PRINT_OPTION')(new PrintOption({ ...params }))
  }

  protected printErrorNotice(error) {
    const content = (() => {
      if (!error.response?.data) {
        // refac 기간 TODO : sentry 에러 로그 전송 비활성화, 에러 로깅 필요한 부분인지 확인
        // monitoringModule.sendMessage(`printError: ${JSON.stringify(error.response)}`)

        return (
          <div>
            <p>서버 연결이 원활하지 않습니다.</p>
            <p>잠시 후 다시 시도하거나</p>
            <p>고객센터(1670-1930)에 문의해주세요.</p>
          </div>
        )
      }
      const { data, message } = error.response.data

      switch (data) {
        case '400':
          return (
            <div>
              <p>연결할 수 있는 프린터가 없습니다.</p>
              <p>프린터를 설치해주세요.</p>
            </div>
          )
        case '404':
          // 일렉트론이 설치 안되있을 떄
          if (message === 'No Agent') {
            modalService.openModal(<NoAgent />, { hasCloseButton: false })
            return
          } else {
            // 프린터가 없을 때 : 'No Printer'
            return (
              <div>
                <p>연결된 프린터가 없습니다.</p>
                <p>{commonRepo.appKorName} PC 버전을 종료 후 다시 실행하거나</p>
                <p>PC버전이 없을 경우 설치해주세요.</p>
              </div>
            )
          }
        default:
          // 500: 내부 서버 에러
          return (
            <div>
              <p>서버 연결이 원활하지 않습니다.</p>
              <p>잠시 후 다시 시도하거나</p>
              <p>고객센터(1670-1930)에 문의해주세요.</p>
            </div>
          )
      }
    })()

    if (content) {
      modalService.openConfirmModal({
        content,
        onClick: modalService.closeModal,
        actionButtonText: '확인',
        hasCancelButton: false,
      })
    }
  }
}
